import type { AgoraArticleFull, AgoraAuthorCard } from "./types";

export const SITE_URL = "https://jurivia.fr";

/** URL canonique d'une analyse : un seul niveau, slug unique à l'échelle du site. */
export function articleUrl(slug: string) {
  return `${SITE_URL}/agora/${slug}`;
}

export function categoryUrl(slug: string) {
  return `${SITE_URL}/agora/theme/${slug}`;
}

export function authorUrl(slug: string) {
  return `${SITE_URL}/auteurs/${slug}`;
}

export function truncate(text: string, max = 155) {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length <= max ? clean : `${clean.slice(0, max - 1).trimEnd()}…`;
}

type MetaEntry = Record<string, string>;

/** Construit les balises meta d'une page Agora (title, description, OG, Twitter). */
export function buildHeadMeta(options: {
  title: string;
  description: string;
  url: string;
  image?: string | null;
  type?: "article" | "website" | "profile";
  publishedAt?: string | null;
  modifiedAt?: string | null;
  noindex?: boolean;
}) {
  const meta: MetaEntry[] = [
    { title: options.title },
    { name: "description", content: options.description },
    { property: "og:type", content: options.type ?? "article" },
    { property: "og:title", content: options.title },
    { property: "og:description", content: options.description },
    { property: "og:url", content: options.url },
    { property: "og:locale", content: "fr_FR" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: options.title },
    { name: "twitter:description", content: options.description },
  ];
  if (options.image) {
    meta.push({ property: "og:image", content: options.image });
    meta.push({ name: "twitter:image", content: options.image });
  }
  if (options.publishedAt) {
    meta.push({ property: "article:published_time", content: options.publishedAt });
  }
  if (options.modifiedAt) {
    meta.push({ property: "article:modified_time", content: options.modifiedAt });
  }
  if (options.noindex) {
    // Contenus de démonstration : ni indexation, ni transmission de popularité.
    meta.push({ name: "robots", content: "noindex, nofollow" });
    meta.push({ name: "googlebot", content: "noindex, nofollow" });
  }
  return {
    meta,
    links: [
      { rel: "canonical", href: options.url },
      { rel: "alternate", hrefLang: "fr-FR", href: options.url },
      { rel: "alternate", hrefLang: "x-default", href: options.url },
    ],
  };
}

const ORGANIZATION = {
  "@type": "Organization",
  name: "Juriv'IA",
  url: SITE_URL,
  logo: `${SITE_URL}/pwa-512x512.png`,
};

/**
 * JSON-LD sûr pour une balise <script> rendue par le serveur : TanStack insère
 * `children` tel quel (dangerouslySetInnerHTML) et JSON.stringify n'échappe pas
 * « </script> ». Une bio d'auteur contenant « </script><script>… » exécutait du
 * code chez chaque visiteur (audit du 17/10/2026). `<`, `>`, `&` et les
 * séparateurs de ligne Unicode sont échappés : le JSON reste identique une fois lu.
 */
export function jsonLdSur(donnees: unknown): string {
  return JSON.stringify(donnees)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

export function breadcrumbJsonLd(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function articleJsonLd(article: AgoraArticleFull) {
  const url = articleUrl(article.slug);
  const graph: Array<Record<string, unknown>> = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: truncate(article.short_answer ?? article.primary_question ?? article.title, 300),
      url,
      mainEntityOfPage: url,
      inLanguage: "fr-FR",
      datePublished: article.published_at ?? undefined,
      dateModified: article.updated_at,
      articleSection: article.category?.name,
      keywords: article.keywords.join(", ") || undefined,
      image: article.cover_image_url ?? undefined,
      author: article.author
        ? {
            "@type": "Person",
            name: article.author.display_name,
            jobTitle: article.author.profession ?? undefined,
            url: authorUrl(article.author.slug),
            worksFor: article.author.organization_name
              ? { "@type": "Organization", name: article.author.organization_name }
              : undefined,
          }
        : undefined,
      publisher: ORGANIZATION,
    },
  ];

  if (article.faqs.length > 0) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: article.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    });
  }

  graph.push(
    breadcrumbJsonLd([
      { name: "Agora", url: `${SITE_URL}/agora` },
      {
        name: article.category?.name ?? "Analyses",
        url: categoryUrl(article.category?.slug ?? "general"),
      },
      { name: article.title, url },
    ]),
  );

  return graph;
}

export function authorJsonLd(author: AgoraAuthorCard) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Person",
      name: author.display_name,
      jobTitle: author.profession ?? undefined,
      description: author.bio ?? undefined,
      url: authorUrl(author.slug),
      image: author.avatar_url ?? undefined,
      knowsAbout: author.domains.length > 0 ? author.domains : undefined,
      worksFor: author.organization_name
        ? { "@type": "Organization", name: author.organization_name }
        : undefined,
    },
    breadcrumbJsonLd([
      { name: "Auteurs", url: `${SITE_URL}/auteurs` },
      { name: author.display_name, url: authorUrl(author.slug) },
    ]),
  ];
}
